import { useState, useCallback } from "react";
import { useCreate, useNotify, useRefresh } from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import { X, Plus, Loader2, Store } from "lucide-react";

interface CreateVendorDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (id: string) => void;
}

export function CreateVendorDialog({ open, onClose, onCreated }: CreateVendorDialogProps) {
  const [create] = useCreate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [creating, setCreating] = useState(false);

  const [form, setForm] = useState({
    name: "",
    contact_name: "",
    email: "",
    phone: "",
    payment_terms: "net_30",
  });

  const handleCreate = useCallback(async () => {
    if (!form.name.trim()) {
      notify("Vendor name is required", { type: "warning" });
      return;
    }
    setCreating(true);
    try {
      const result = await create(
        "vendors",
        {
          data: {
            name: form.name.trim(),
            contact_name: form.contact_name.trim(),
            email: form.email.trim() || null,
            phone: form.phone.trim() || null,
            payment_terms: form.payment_terms,
          },
        },
        { returnPromise: true }
      );

      notify("Vendor added", { type: "success" });
      refresh();
      const newId = (result as { data?: { id?: string } })?.data?.id;
      if (newId && onCreated) onCreated(newId);
      onClose();
      setForm({ name: "", contact_name: "", email: "", phone: "", payment_terms: "net_30" });
    } catch (err) {
      notify(`Failed: ${(err as Error).message}`, { type: "error" });
    } finally {
      setCreating(false);
    }
  }, [form, create, notify, refresh, onCreated, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <Card className="w-full max-w-md mx-4 shadow-xl">
        <CardContent className="p-0">
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-base font-semibold flex items-center gap-2">
              <Store className="w-4 h-4" /> Add Vendor
            </h3>
            <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="p-4 space-y-3">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Company name *</label>
              <input type="text" value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="w-full px-3 py-2 border rounded text-sm" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Contact person</label>
              <input type="text" value={form.contact_name}
                onChange={(e) => setForm((f) => ({ ...f, contact_name: e.target.value }))}
                className="w-full px-3 py-2 border rounded text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Phone</label>
                <input type="text" value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  className="w-full px-3 py-2 border rounded text-sm" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Email</label>
                <input type="text" value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  className="w-full px-3 py-2 border rounded text-sm" />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Payment terms</label>
              <select value={form.payment_terms}
                onChange={(e) => setForm((f) => ({ ...f, payment_terms: e.target.value }))}
                className="w-full px-3 py-2 border rounded text-sm">
                <option value="cod">COD</option>
                <option value="net_15">Net 15</option>
                <option value="net_30">Net 30</option>
                <option value="net_45">Net 45</option>
                <option value="net_60">Net 60</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2 p-4 border-t bg-gray-50 rounded-b-lg">
            <button onClick={onClose} disabled={creating}
              className="px-3 py-1.5 rounded text-sm font-medium border text-gray-700 hover:bg-gray-100">
              Cancel
            </button>
            <button onClick={handleCreate} disabled={creating || !form.name.trim()}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50">
              {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              Add
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
